import { useState } from "react";
import { Instagram, Youtube, Twitch, ArrowUpRight, Play } from "lucide-react";
import { TikTokIcon, TOK_SOCIAL_HANDLES } from "@/components/SocialFunnel";
import { Container, FadeIn } from "@/components/Section";
import { IMG } from "@/lib/data";

/**
 * Unified TOK content ecosystem — cinematic follow surface.
 *
 * Purpose is unambiguous: get visitors following TOK on the four
 * platforms.  Structure:
 *
 *   Headline + 1 sentence
 *   1 large featured cinematic card (clickable into the ecosystem)
 *   4 elegant platform "follow" buttons with @templeofkulture
 *
 * No filter bar.  No supporting card grid.  No repeated icon rows.
 */

const PLATFORMS = [
  { key: "tiktok", label: "TikTok", handle: "@templeofkulture", Icon: TikTokIcon, href: TOK_SOCIAL_HANDLES.tiktok },
  { key: "instagram", label: "Instagram", handle: "@templeofkulture", Icon: Instagram, href: TOK_SOCIAL_HANDLES.instagram },
  { key: "youtube", label: "YouTube", handle: "@templeofkulture", Icon: Youtube, href: TOK_SOCIAL_HANDLES.youtube },
  { key: "twitch", label: "Twitch", handle: "templeofkulture", Icon: Twitch, href: TOK_SOCIAL_HANDLES.twitch },
];

const DEFAULT_FEATURED = {
  platform: "tiktok",
  title: "LEVEL UP — studio cuts",
  caption: "Behind the lens of the 16–25 cohort.",
  image: IMG.dance1,
};

export default function SocialEcosystem({
  eyebrow = "Follow the ecosystem",
  title = "Enter the ecosystem beyond the stage.",
  copy = "Follow TOK across Instagram, TikTok, YouTube and Twitch.",
  featured = DEFAULT_FEATURED,
  className = "",
  testId = "social-ecosystem",
}) {
  return (
    <section
      data-testid={testId}
      className={`relative bg-black border-y border-amber-500/10 overflow-hidden ${className}`}
    >
      {/* Soft amber bloom */}
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[28rem] w-[28rem] rounded-full bg-amber-400/[0.05] blur-[120px]" />
      <div className="pointer-events-none absolute right-0 bottom-0 h-[22rem] w-[22rem] rounded-full bg-amber-400/[0.04] blur-[140px]" />

      <Container className="relative py-24 sm:py-28 lg:py-32">
        {/* Header — centred, minimal, aspirational */}
        <FadeIn className="max-w-2xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.45em] text-amber-400/80 font-bold">
            {eyebrow}
          </p>
          <h2 className="mt-6 font-display font-black uppercase tracking-tighter leading-[1.0] text-white text-3xl sm:text-4xl lg:text-5xl">
            {title}
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed text-base sm:text-lg">
            {copy}
          </p>
        </FadeIn>

        {/* Single cinematic featured card — centred, large breathing room */}
        <FadeIn delay={0.08}>
          <div className="mt-14 sm:mt-16 max-w-3xl mx-auto">
            <FeaturedCard moment={featured} />
          </div>
        </FadeIn>

        {/* Follow buttons — the actionable surface.  Bigger, labelled,
            unambiguous.  This is the ONLY social CTA row on the section. */}
        <FadeIn delay={0.18}>
          <div className="mt-14 sm:mt-16 max-w-3xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {PLATFORMS.map(({ key, ...rest }) => (
                <FollowButton key={key} {...rest} />
              ))}
            </div>
            <p
              data-testid="social-ecosystem-handle"
              className="mt-6 text-center text-[10px] uppercase tracking-[0.4em] text-zinc-500 font-bold"
            >
              @templeofkulture · everywhere
            </p>
          </div>
        </FadeIn>
      </Container>
    </section>
  );
}

function FeaturedCard({ moment }) {
  const [hover, setHover] = useState(false);
  const platform = PLATFORMS.find((p) => p.key === moment.platform);
  if (!platform) return null;
  const { Icon, href } = platform;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`social-card-${moment.platform}-featured`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative block overflow-hidden bg-zinc-950 border border-white/[0.06] transition-all duration-700 aspect-[4/5] sm:aspect-[16/10]"
      style={{
        boxShadow: hover
          ? "0 30px 80px -20px rgba(251,191,36,0.25), 0 0 0 1px rgba(251,191,36,0.35)"
          : "0 0 0 1px rgba(255,255,255,0.04)",
      }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[1800ms] ease-out"
        style={{
          backgroundImage: `url(${moment.image})`,
          transform: hover ? "scale(1.06)" : "scale(1.01)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
      <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/40 to-transparent" />

      <div className="absolute top-5 right-5 inline-flex h-9 w-9 items-center justify-center bg-black/50 backdrop-blur-md text-amber-400 ring-1 ring-amber-400/30">
        <Icon className="h-3.5 w-3.5" />
      </div>

      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-full border border-amber-400/60 bg-black/30 backdrop-blur-sm transition-all duration-500 ${
            hover ? "scale-110 border-amber-300 bg-amber-400/10" : "scale-100"
          }`}
        >
          <Play className="h-5 w-5 text-amber-300 ml-0.5" />
        </div>
      </div>

      <div className="absolute left-0 right-0 bottom-0 p-6 sm:p-7 lg:p-9">
        <p className="font-display font-black text-white leading-[1.05] uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          {moment.title}
        </p>
        <p className="mt-3 text-zinc-300/90 leading-relaxed text-sm sm:text-base max-w-md">
          {moment.caption}
        </p>
      </div>
    </a>
  );
}

function FollowButton({ label, handle, Icon, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Follow Temple of Kulture on ${label} (${handle})`}
      data-testid={`social-follow-${label.toLowerCase()}`}
      className="group relative flex items-center gap-4 border border-white/10 hover:border-amber-400/80 bg-white/[0.02] hover:bg-amber-400/[0.06] transition-all duration-300 px-5 py-5"
    >
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center bg-black border border-amber-400/30 group-hover:border-amber-300 transition-colors text-amber-400">
        <Icon className="h-4 w-4" />
      </span>
      <span className="text-sm font-display font-bold text-white leading-none flex-1">
        {label}
      </span>
      <ArrowUpRight className="h-4 w-4 text-zinc-600 group-hover:text-amber-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0" />
    </a>
  );
}
